import {
  Box,
  Checkbox,
  Link,
  ListItemAvatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';
import Pagination from '../Pagination/Pagination';
import { RowNoData } from '../styled';
import './Table.scss';
import { DatePattern, Regex } from '@utils/constants';
import { ListMeta } from '@interfaces/apis/response-data';
import { isDatetime } from '@utils/helpers/validate';
import { formatDate } from '@utils/helpers';
import AppTooltip from '../Tooltip/Tooltip';
import { ImageNotSupported } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { setCheckedIdsState } from '@redux/reducers/appSlice';
import { ButtonStatus } from '..';

type RoleType = 'owner' | 'coOwner' | 'system' | 'view' | 'search';

interface AppTableProps {
  uncheckable?: boolean;
  unPagination?: boolean;
  isVisible?: boolean;
  height?: string;
  header?: ITableHeader[];
  data?: ITableData[];
  dataCheckAll?: ITableData[];
  actionColumns?: string;
  paginationMeta?: ListMeta;
  role?: RoleType;
  isUnderRole?: (
    row: ITableData,
    uncheckable: boolean,
    role?: RoleType
  ) => boolean;
  onClickCell?: (row: ITableData, column: string) => void;
  onChangePage?: (page: string) => void;
  onDelete?: (ids: string[]) => void;
  isStatusHeight?: boolean;
  maxlength?: number;
  onClickStatus?: (id: string) => void;
  hoverImageEnable?: boolean;
  driverStatus?: boolean;
  checkOvertime?: boolean;
  resetCheckedSignal?: number;
}

const AppTable: FC<AppTableProps> = ({
  uncheckable = false,
  unPagination = false,
  isVisible = false,
  header = [],
  data = [],
  dataCheckAll = [],
  actionColumns,
  paginationMeta,
  role,
  isUnderRole,
  onClickCell,
  onChangePage,
  onDelete,
  height = '100%',
  isStatusHeight = false,
  maxlength,
  onClickStatus,
  hoverImageEnable = false,
  driverStatus = false,
  checkOvertime = false,
  resetCheckedSignal,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isEnableClickRow = actionColumns === 'all';
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const idSignature = data.map(item => String(item.id)).join(',');

  useEffect(() => {
    setCheckedIds([]);
  }, [idSignature]);

  // Sync to Redux
  useEffect(() => {
    dispatch(setCheckedIdsState(checkedIds));
  }, [checkedIds]);

  useEffect(() => {
    setCheckedIds([]);
  }, [resetCheckedSignal]);

  const handleCheckAll = (_: React.ChangeEvent, checked: boolean) => {
    if (checked) {
      if (dataCheckAll && dataCheckAll.length) {
        setCheckedIds(dataCheckAll.map(({ id }) => String(id)));
      } else {
        setCheckedIds(data.map(({ id }) => String(id)));
      }
    } else {
      setCheckedIds([]);
    }
  };

  const handleCheck = (event: React.ChangeEvent, checked: boolean) => {
    const id = event.target.id;
    if (checked) {
      setCheckedIds([...checkedIds, id]);
    } else {
      setCheckedIds(checkedIds.filter(cId => cId !== id));
    }
  };

  const handleClickRow = (row: ITableData) => {
    if (isEnableClickRow && onClickCell) onClickCell(row, 'all');
  };

  return (
    <div className='AppTable'>
      <TableContainer
        className='tableContainer'
        sx={{ height: height, WebkitOverflowScrolling: 'touch' }}
        data-is-set-height={isStatusHeight}
      >
        <Table stickyHeader>
          {header.length && (
            <TableHead className='tableHeader'>
              <TableRow sx={{}}>
                {!uncheckable && (
                  <TableCell
                    sx={{
                      width: '0% !important',
                      padding: '0',
                      borderBottom: ' 4px solid rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    <Checkbox
                      disabled={
                        !!role ||
                        (dataCheckAll ? !dataCheckAll.length : !data.length)
                      }
                      sx={{ '& .MuiSvgIcon-root': { fontSize: 22 } }}
                      checked={
                        !!checkedIds.length &&
                        (dataCheckAll
                          ? checkedIds.length === dataCheckAll.length
                          : checkedIds.length === data.length)
                      }
                      indeterminate={
                        !!checkedIds.length &&
                        (dataCheckAll
                          ? checkedIds.length < dataCheckAll.length
                          : checkedIds.length < data.length)
                      }
                      onChange={handleCheckAll}
                    />
                  </TableCell>
                )}
                {header.map(({ label, width }) => (
                  <TableCell
                    key={label}
                    sx={{
                      width: `${width}%`,
                      whiteSpace: 'nowrap',
                      borderBottom: ' 4px solid rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    {label && t(label)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={header.length + (uncheckable ? 0 : 1)}
                  align='center'
                  data-is-bottom={data.length === 0 ? 'true' : 'undefine'}
                  sx={{ textAlign: 'center' }}
                >
                  <RowNoData>{t('common.no_data_display')}</RowNoData>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow
                  key={`${row.id}-${index}`}
                  hover={isEnableClickRow}
                  sx={{
                    borderRadius: '4px',
                    height: '70px',
                  }}
                >
                  {!uncheckable && (
                    <TableCell
                      sx={{ width: '0% !important', padding: '100px' }}
                    >
                      <Checkbox
                        id={String(row.id)}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 22 } }}
                        checked={checkedIds.includes(String(row.id))}
                        onChange={handleCheck}
                        onClick={e => e.stopPropagation()}
                        disabled={
                          isUnderRole && isUnderRole(row, uncheckable, role)
                        }
                      />
                    </TableCell>
                  )}
                  {header.map(({ column, translate, renderActions, width }) => {
                    const rowData = row[column];
                    const imageKeywords = [
                      'imageurlregister',
                      'imageUrlAccess',
                    ];

                    const isImage =
                      (typeof rowData === 'string' &&
                        rowData.match(/\.(jpeg|jpg|gif|png|webp)(\?.*)?$/i)) ||
                      imageKeywords.some(keyword => column.includes(keyword));

                    const isLink =
                      typeof rowData === 'string' &&
                      rowData.match(Regex.RULE.LINK);

                    const renderText = (text: string) => {
                      let textFormat = text;
                      if (isDatetime(textFormat))
                        textFormat = formatDate(
                          textFormat,
                          DatePattern.DATE_TIME
                        );

                      if (
                        typeof textFormat === 'string' &&
                        !!maxlength &&
                        textFormat.length > maxlength
                      ) {
                        const textSlice = textFormat.slice(0, maxlength);
                        return (
                          <AppTooltip title={textFormat} arrow>
                            <span>{`${textSlice}...`}</span>
                          </AppTooltip>
                        );
                      }
                      return textFormat;
                    };
                    const renderRowText =
                      typeof rowData === 'string' && !isLink
                        ? renderText(rowData)
                        : rowData;
                    const isOverTime = row.isDriverOverTime === true;
                    const shouldHighlight = checkOvertime && isOverTime;
                    return (
                      <TableCell
                        key={column}
                        onClick={() => {
                          if (renderActions) {
                            return;
                          } else {
                            handleClickRow(row);
                          }
                        }}
                        sx={{
                          width: `${width}%`,
                          maxWidth: '150',
                          height: '70px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          background: shouldHighlight ? '#FFF4A4' : undefined,
                        }}
                        data-is-bottom={data.length > 0 ? 'false' : 'undefined'}
                      >
                        {column === 'status' ? (
                          driverStatus ? (
                            <ButtonStatus
                              isTable
                              row={row}
                              status={row.status}
                              onClickStatus={onClickStatus}
                              sx={{ width: 140 }}
                            />
                          ) : row.status === '1' || row.status === '入館中' ? (
                            onClickStatus ? (
                              <Button
                                onClick={() => onClickStatus?.(row.id)}
                                title={t(
                                  'history_driver_management.currently_in_the_building'
                                )}
                                typeName='danger'
                              />
                            ) : (
                              <Box
                                sx={{
                                  backgroundColor: '#EA4335',
                                  color: '#fff',
                                  padding: '6px 15px',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontWeight: 510,
                                  textAlign: 'center',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  display: 'flex',
                                  boxShadow: 2,
                                  whiteSpace: 'nowrap',
                                  width: '88px',
                                  height: '50px',
                                }}
                              >
                                {t('common.currently_in_the_building')}
                              </Box>
                            )
                          ) : (
                            row.status === 'pending' &&
                            (onClickStatus ? (
                              <Button
                                onClick={() => onClickStatus?.(row.id)}
                                title={t('common.pending_approval')}
                                sx={{
                                  background: '#F9BD06',
                                  borderRadius: '4px',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  backgroundColor: '#F9BD06',
                                  color: '#fff',
                                  padding: '6px 15px',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  fontWeight: 510,
                                  textAlign: 'center',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  display: 'flex',
                                  boxShadow: 2,
                                  whiteSpace: 'nowrap',
                                  width: '88px',
                                  height: '50px',
                                }}
                              >
                                {t('common.pending_approval')}
                              </Box>
                            ))
                          )
                        ) : renderActions ? (
                          renderActions(row, onClickCell)
                        ) : isImage ? (
                          <div
                            style={{
                              border: '1px solid #000',
                              height: 60,
                              width: 80,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                            }}
                          >
                            {rowData === null || rowData === '' ? (
                              <ImageNotSupported
                                sx={{
                                  width: '80px',
                                  height: '60px',
                                  color: '#BABABA',
                                }}
                              />
                            ) : (
                              <div className='imgHoverContainer'>
                                <ListItemAvatar className='imgHoverWrapper'>
                                  <img
                                    src={rowData}
                                    alt={column}
                                    className='tinyImg'
                                  />
                                </ListItemAvatar>
                                {hoverImageEnable && (
                                  <div className='previewOverlay'>
                                    <img
                                      src={rowData}
                                      alt={column}
                                      className='fullImg'
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : actionColumns?.split(',')?.includes(column) ? (
                          isUnderRole && isUnderRole(row, uncheckable, role) ? (
                            translate ? (
                              t(renderRowText)
                            ) : (
                              renderRowText
                            )
                          ) : (
                            <Link
                              target='_blank'
                              underline={isLink ? 'hover' : 'none'}
                              component={isLink ? 'a' : 'button'}
                              href={renderRowText}
                              onClick={() => onClickCell?.(row, column)}
                            >
                              {translate ? t(renderRowText) : renderRowText}
                            </Link>
                          )
                        ) : translate ? (
                          t(renderRowText)
                        ) : (
                          renderRowText
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className='AppTable__footer'>
        {!isVisible && !uncheckable && (
          <Button
            color='error'
            disabled={!checkedIds.length}
            title='common.button_delete'
            onClick={() => onDelete?.(checkedIds)}
          />
        )}
        {!unPagination && (
          <Pagination meta={paginationMeta} onChange={onChangePage} />
        )}
      </div>
    </div>
  );
};

export default AppTable;
